import { FetchSmsParams, PaginatedSmsResponse, SmsMessage } from "utils/types/SmsReaderTypes";
import { apiSrcUrl } from "../ApiRoutes";
import { getToken } from "utils/helper";
import { ApiResponse } from "utils/types/API";
import { ApiService } from "../apiservices";

/**
 * Fetches a paginated, sorted, and filtered list of SMS messages using the ApiService.
 * @param params - The query parameters for the API call.
 * @returns A promise that resolves to the API response containing messages and pagination metadata.
 */
export const fetchPaginatedSms = async (
  params: FetchSmsParams
): Promise<PaginatedSmsResponse> => {
  if (!apiSrcUrl) {
    throw new Error("API source URL is not configured.");
  }
  const token = getToken();
  if (!token) {
    throw new Error("Authentication token not found.");
  }

  const query = new URLSearchParams({
    page: String(params.page + 1),
    limit: String(params.limit),
    sortBy: params.sortBy || "date",
    order: params.order || "desc",
  });

  if (params.searchQuery) {
    query.append("searchQuery", params.searchQuery);
  }
  if (params.filterByCountry) {
    query.append("filterByCountry", params.filterByCountry);
  }

  const fullUrl = `${apiSrcUrl}/private/sms?${query.toString()}`;

  const { data: responseData } = await ApiService.get<ApiResponse<PaginatedSmsResponse>>(fullUrl, {
    Authorization: `Bearer ${token}`,
  });

  return responseData.data; 
};

/**
 * Fetches a single SMS message by its ID using the ApiService.
 * @param id - The ID of the message.
 * @param countryCode - The optional country code for a more specific search.
 * @returns A promise that resolves to a single SMS message.
 */
export const fetchSingleMessage = async (
  id: string,
  countryCode?: string
): Promise<SmsMessage> => {
  if (!apiSrcUrl) {
    throw new Error("API source URL is not configured.");
  }
  const token = getToken();
  if (!token) {
    throw new Error("Authentication token not found.");
  }

  const url = new URL(`${apiSrcUrl}/private/sms/${id}`);
  if (countryCode) {
    url.searchParams.append('countryCode', countryCode);
  }

  const { data: responseData } = await ApiService.get<ApiResponse<SmsMessage>>(url.toString(), {
    Authorization: `Bearer ${token}`,
  });

  return responseData.data; 
};